import React from "react";
import CommonBanner from "../../components/CommonBanner/CommonBanner";
import EcoBeeInfographics from "../../assets/images/eco-bee/eco-bee-infographics.png";
import EcoBeeSubsection from "./EcoBeeSubsection";
import EcoBeeSensor from "../../assets/images/eco-bee/Advanced IoT Sensors.png";
import Dashboard from "../../assets/images/eco-bee/AI-Powered Analytics Dashboard.png";
import LiveStraming from "../../assets/images/eco-bee/24-7 HD Livestreaming.png";
import HarvestOwnership from "../../assets/images/eco-bee/Harvest Ownership.png";
import MobileAppIntegration from "../../assets/images/eco-bee/Mobile App Integration.png";
import AutomatedHarvestTracking from "../../assets/images/eco-bee/Automated Harvest Tracking.png";
import Investor from "../../assets/images/eco-bee/investor.png";

// Component to render the Eco-Bee page with multiple informative subsections
export const EcoBee = () => {
  // Array of subsection configurations used to dynamically render each EcoBeeSubsection component
  const subsections = [
    {
      // Introductory section describing the Eco-Bee concept
      image: EcoBeeInfographics,
      description: `Apischain's Eco-Bee is a smart, connected beehive designed for individual eco-contributors and businesses aiming to support sustainable agriculture and biodiversity. This innovative system combines IoT sensors, AI analytics, and real-time monitoring to provide comprehensive insights into hive health and productivity.`,
      alt: "Eco-bee-infographics",
    },
    {
      // Subsection for IoT sensors in the beehive
      image: EcoBeeSensor,
      description: `Eco-Bee hives are equipped with a range of IoT sensors that monitor critical environmental and behavioral parameters:`,
      featureTitle: "Advanced IoT Sensors",
      features: [
        {
          title: "Temperature",
          content: "Tracks internal and external temperatures to ensure optimal conditions for the colony.",
        },
        {
          title: "Humidity",
          content: "Monitors moisture levels within the hive, which is crucial for brood development and honey preservation.",
        },
        {
          title: "Weight",
          content: "Measures the hive's weight to estimate honey production and detect potential issues like swarming.",
        },
        {
          title: "Sound",
          content: "Captures acoustic data to assess bee activity levels and detect anomalies.",
        },
        {
          title: "Bee Flight Patterns",
          content: "Utilizes motion sensors to observe bee foraging behavior and hive entrance activity.",
        },
      ],
      alt: "IoT Sensor",
    },
    {
      // Subsection for AI-powered analytics dashboard
      image: Dashboard,
      description: `The Eco-Bee system features an AI-driven analytics dashboard that processes data from the sensors to provide:`,
      featureTitle: "AI-Powered Analytics Dashboard",
      features: [
        { title: "Hive Health Monitoring", content: "Evaluates the overall well-being of the colony." },
        { title: "Productivity Trends", content: "Tracks honey production and brood development over time." },
        { title: "Alert System", content: "Notifies users of potential issues such as temperature fluctuations, humidity imbalances, or abnormal bee activity." },
        { title: "Predictive Analytics", content: "Forecasts future hive conditions and productivity, aiding in proactive management." },
      ],
      alt: "AI-Powered Analytics Dashboard",
    },
    {
      // Subsection for live camera feed feature
      image: LiveStraming,
      description: `Each Eco-Bee hive is equipped with a high-definition camera that offers:`,
      featureTitle: "24/7 HD Livestreaming",
      features: [
        { title: "Real-Time Viewing", content: "Allows users to observe hive activity at any time." },
        { title: "Playback Functionality", content: "Enables review of past activities to analyze bee behavior and hive conditions." },
        { title: "Educational Tool", content: "Serves as a resource for learning about bee behavior and hive management." },
      ],
      alt: "24/7 HD Livestreaming",
    },
    {
      // Subsection for automated harvest tracking
      image: AutomatedHarvestTracking,
      description: `The system automatically monitors and records honey production, providing:`,
      featureTitle: "Automated Harvest Tracking",
      features: [
        { title: "Real-Time Updates", content: "Keeps users informed about the status of honey production." },
        { title: "Harvest Forecasting", content: "Estimates optimal harvest times based on hive conditions and productivity trends." },
        { title: "Yield Analytics", content: "Analyzes harvest data to assess the efficiency and health of the colony." },
      ],
      alt: "Automated Harvest Tracking",
    },
    {
      // Subsection describing mobile app capabilities
      image: MobileAppIntegration,
      description: `Eco-Bee offers a mobile application that provides:`,
      featureTitle: "Mobile App Integration",
      features: [
        { title: "Remote Monitoring", content: "Access to live data and alerts from anywhere." },
        { title: "Data Visualization", content: "Interactive graphs and charts for easy interpretation of hive metrics." },
        { title: "Push Notifications", content: "Instant alerts for critical hive events or conditions." },
        { title: "User-Friendly Interface", content: "Designed for both novice and experienced beekeepers." },
      ],
      alt: "Mobile App Integration",
    },
    {
      // Subsection on honey ownership and harvest options
      image: HarvestOwnership,
      description: `Users retain 100% ownership of the honey produced by their Eco-Bee hive, with options for:`,
      featureTitle: "Harvest Ownership",
      features: [
        { title: "Remote Monitoring", content: "Access to live data and alerts from anywhere." },
        { title: "Direct Harvesting", content: "Users can choose to harvest the honey themselves." },
        { title: "Collection Services", content: "Optional services are available for honey collection and processing." },
      ],
      alt: "Harvest Ownership",
    },
    {
      // Subsection targeting different user personas
      image: Investor,
      description: `Eco-Bee is designed to cater to:`,
      featureTitle: "Ideal for Passive Supporters and Remote Investors",
      features: [
        { title: "Eco-Conscious Individuals", content: "Those looking to support sustainable agriculture without active involvement." },
        { title: "Remote Investors", content: "Individuals interested in investing in beekeeping without the need for hands-on management." },
        { title: "Educational Institutions", content: "Schools and universities aiming to educate students about environmental conservation and apiculture." },
      ],
      alt: "Ideal for Passive Supporters and Remote Investors",
    },
  ];

  return (
    <>
      {/* Page banner */}
      <CommonBanner bannerText="Eco-Bee" />

      {/* Dynamically render each subsection from the array above */}
      {subsections.map((section, idx) => (
        <EcoBeeSubsection key={idx} {...section} />
      ))}
    </>
  );
};